import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ReportPreviewModal = ({ report, onClose }) => {
  const reportData = report?.report_data || {};

  const handleDownloadPDF = async () => {
    const element = document.getElementById('report-content');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas?.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas?.height * imgWidth) / canvas?.width;

      pdf?.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf?.save(`${report?.report_name}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF');
    }
  };

  const renderFinancialReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-success data-text">
            ${reportData?.totalRevenue?.toFixed(2) || '0.00'}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Transactions</p>
          <p className="text-2xl font-bold text-foreground data-text">
            {reportData?.transactionCount || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Average Transaction</p>
          <p className="text-2xl font-bold text-primary data-text">
            ${reportData?.averageTransaction?.toFixed(2) || '0.00'}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Payment Methods</p>
          <div className="text-sm text-foreground">
            <p>Cash: {reportData?.paymentMethods?.cash || 0}</p>
            <p>Card: {reportData?.paymentMethods?.creditCard || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMembershipReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">New Members</p>
          <p className="text-2xl font-bold text-success data-text">
            {reportData?.newMembers || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Active Members</p>
          <p className="text-2xl font-bold text-primary data-text">
            {reportData?.activeMembers || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Churned Members</p>
          <p className="text-2xl font-bold text-error data-text">
            {reportData?.churnedMembers || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Retention Rate</p>
          <p className="text-2xl font-bold text-foreground data-text">
            {reportData?.retentionRate?.toFixed(1) || '0.0'}%
          </p>
        </div>
      </div>
    </div>
  );

  const renderInventoryReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Total Products</p>
          <p className="text-2xl font-bold text-foreground data-text">
            {reportData?.totalProducts || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Low Stock Items</p>
          <p className="text-2xl font-bold text-warning data-text">
            {reportData?.lowStockCount || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Out of Stock</p>
          <p className="text-2xl font-bold text-error data-text">
            {reportData?.outOfStockCount || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Inventory Value</p>
          <p className="text-2xl font-bold text-success data-text">
            ${reportData?.totalInventoryValue?.toFixed(2) || '0.00'}
          </p>
        </div>
      </div>
      {reportData?.lowStockItems && reportData?.lowStockItems?.length > 0 && (
        <div>
          <h4 className="font-semibold text-foreground mb-3">Low Stock Alert</h4>
          <div className="space-y-2">
            {reportData?.lowStockItems?.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <span className="text-sm text-foreground">{item?.name}</span>
                <span className="text-sm font-medium text-warning data-text">{item?.stock} units</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderOperationsReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Check-ins</p>
          <p className="text-2xl font-bold text-foreground data-text">
            {reportData?.checkIns || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Class Attendance</p>
          <p className="text-2xl font-bold text-primary data-text">
            {reportData?.classAttendance || 0}
          </p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Staff Activity</p>
          <p className="text-2xl font-bold text-accent data-text">
            {reportData?.staffActivity || 0}
          </p>
        </div>
      </div>
    </div>
  );

  const renderReportContent = () => {
    switch (report?.report_type) {
      case 'financial':
        return renderFinancialReport();
      case 'membership':
        return renderMembershipReport();
      case 'inventory':
        return renderInventoryReport();
      case 'operations':
        return renderOperationsReport();
      default:
        return (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Report data not available</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">{report?.report_name}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {report?.date_range_start && report?.date_range_end
                ? `${format(new Date(report.date_range_start), 'MMM d, yyyy')} - ${format(new Date(report.date_range_end), 'MMM d, yyyy')}`
                : `Generated on ${format(new Date(report?.created_at), 'MMM d, yyyy h:mm a')}`
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadPDF}
              iconName="Download"
              iconPosition="left"
            >
              Download PDF
            </Button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={20} color="var(--color-foreground)" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div id="report-content" className="bg-white p-6 rounded-lg">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{report?.report_name}</h1>
              <p className="text-sm text-gray-600">
                Report Period: {report?.date_range_start && report?.date_range_end
                  ? `${format(new Date(report.date_range_start), 'MMM d, yyyy')} - ${format(new Date(report.date_range_end), 'MMM d, yyyy')}`
                  : 'Current Data'
                }
              </p>
            </div>
            {renderReportContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewModal;